import React from "react";
import classNames from "classnames";
import { pick, omit } from "lodash";
import { FormFeedback } from "reactstrap";
import { WrappedFieldProps } from "redux-form";

import { FormGroup, FormGroupProps } from "../FormGroup";
import { DatesObject, PresetDateRangePicker, PresetDateRangePickerProps } from "../../PresetDateRangePicker";

interface FormDateRangeProps extends PresetDateRangePickerProps {
    startDateName: string;
    endDateName: string;
    label: string;
    showLabel?: boolean;
}

// HACK: redux-form types are borked
interface WrappedFieldsProps {
    [name: string]: WrappedFieldProps;
}
type Props = FormDateRangeProps & Omit<WrappedFieldsProps, keyof FormDateRangeProps>;

const FormDateRange = (props: Props): JSX.Element => {
    const {
        startDateName,
        endDateName,
        label,
        showLabel,
        // For fields, the input and meta props are under the field name
        [startDateName]: startDate,
        [endDateName]: endDate,
    } = props;
    const otherProps = omit(props, startDateName, endDateName) as Props;

    if (startDate == null || endDate == null) {
        throw Error("Invalid configuration, cannot find start/end date property(ies)");
    }

    // Sort the group props
    const startDateHasError = startDate.meta.touched && startDate.meta.error != null;
    const endDateHasError = endDate.meta.touched && endDate.meta.error != null;
    const groupProps: FormGroupProps = {
        label,
        hasError: startDateHasError || endDateHasError,
        showLabel,
    };
    // And the pickerProps
    const pickerProps = pick<Pick<Props, keyof PresetDateRangePickerProps>, keyof PresetDateRangePickerProps>(
        otherProps,
        "presets", "earliestDate", "latestDate", "maxDays", "startDate", "startDateId",
        "endDate", "endDateId", "onDatesChange", "onClose", "startDatePlaceholderText",
        "endDatePlaceholderText", "disabled", "required", "readOnly", "screenReaderInputMessage",
        "showClearDates", "showDefaultInputIcon", "customInputIcon", "customArrowIcon",
        "customCloseIcon", "noBorder", "block", "small", "regular", "keepFocusOnInput",
        "renderMonthText", "renderMonthElement", "orientation", "anchorDirection",
        "openDirection", "horizontalMargin", "withPortal", "withFullScreenPortal",
        "appendToBody", "disableScroll", "daySize", "isRTL", "firstDayOfWeek",
        "initialVisibleMonth", "numberOfMonths", "keepOpenOnDateSelect",
        "reopenPickerOnClearDates", "renderCalendarInfo", "calendarInfoPosition",
        "hideKeyboardShortcutsPanel", "verticalHeight", "transitionDuration", "verticalSpacing",
        "navPrev", "navNext", "onPrevMonthClick", "onNextMonthClick", "renderCalendarDay",
        "renderDayContents", "minimumNights", "enableOutsideDays", "isDayBlocked",
        "isDayHighlighted", "displayFormat", "monthFormat", "weekDayFormat", "phrases",
        "dayAriaLabelFormat",
    );

    const { onDatesChange, latestDate } = pickerProps;
    pickerProps.onDatesChange = (dates: DatesObject) => {
        startDate.input.onChange(dates.startDate);
        endDate.input.onChange(dates.endDate);
        if (onDatesChange != null) {
            onDatesChange(dates);
        }
    };

    return (
        <FormGroup {...groupProps}>
            <div className={classNames({
                    "form-date-range": true,
                    "is-invalid": startDateHasError || endDateHasError,
                })}
            >
                <PresetDateRangePicker
                    presets={[]}
                    displayFormat="DD/MM/YYYY"
                    initialVisibleMonth={() => latestDate.clone().subtract(1, "month")}
                    {...pickerProps}
                />
            </div>
            {startDateHasError && <FormFeedback key="start-date-feedback">{startDate.meta.error}</FormFeedback>}
            {endDateHasError && <FormFeedback key="end-date-feedback">{endDate.meta.error}</FormFeedback>}
        </FormGroup>
    );
};

export {
    FormDateRange,
    FormDateRangeProps as FormDateRangeOwnProps,
    Props as FormDateRangeProps,
};
